import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Settings, FileSpreadsheet, Phone, Mail, FileText, Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCustomerCollectionCases } from "@/hooks/useCustomerCollectionCases";
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog";
import { useScreenColumnPreferences } from "@/hooks/useScreenColumnPreferences";
import { LogCallModal, SendEmailModal, RecordPromiseModal } from "@/components/collections/action-modals";

import { formatCurrency, formatDate } from "@/lib/utils";
import { toast } from "sonner";
import { exportCollectionCasesToExcel } from "@/lib/collections-export-utils";
import type { CollectionCase } from "@/types/collections";

const DEFAULT_COLLECTIONS_TAB_COLUMNS: ColumnDefinition[] = [
  { id: 'priority', label: 'Priority', visible: true, order: 0 },
  { id: 'caseId', label: 'Case ID', visible: true, order: 1 },
  { id: 'customerName', label: 'Customer', visible: true, order: 2 },
  { id: 'amount', label: 'Amount', visible: true, order: 3 },
  { id: 'daysInCollection', label: 'Days', visible: true, order: 4 },
  { id: 'lastAction', label: 'Last Action', visible: true, order: 5 },
  { id: 'nextAction', label: 'Next Action', visible: true, order: 6 },
  { id: 'nextActionDate', label: 'Action Due Date', visible: true, order: 7 },
  { id: 'status', label: 'Status', visible: true, order: 8 },
  { id: 'strategy', label: 'Collection Strategy', visible: true, order: 9 },
  { id: 'stage', label: 'Stage', visible: true, order: 10 },
  { id: 'customerId', label: 'Customer ID', visible: false, order: 11 },
  { id: 'customerEmail', label: 'Email', visible: false, order: 12 },
  { id: 'customerPhone', label: 'Phone', visible: false, order: 13 },
  { id: 'riskLevel', label: 'Risk Level', visible: false, order: 14 },
  { id: 'assignedTo', label: 'Assigned To', visible: false, order: 15 },
  { id: 'createdAt', label: 'Created Date', visible: false, order: 16 },
  { id: 'updatedAt', label: 'Last Updated', visible: false, order: 17 },
];

interface CollectionsTabProps {
  customerId: string;
  customerName: string;
  customerPhone?: string;
  accountNumber: string;
  isConsolidatedView?: boolean;
}

export function CollectionsTab({ customerId, customerName, customerPhone, accountNumber, isConsolidatedView = false }: CollectionsTabProps) {
  const navigate = useNavigate();
  const { cases, isLoading } = useCustomerCollectionCases(customerId);
  
  // State
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  
  // Column preferences
  const { columns, visibleColumns, savePreferences, resetToDefaults } = useScreenColumnPreferences('customer-detail-collections', DEFAULT_COLLECTIONS_TAB_COLUMNS);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  
  // Action modals
  const [logCallModalOpen, setLogCallModalOpen] = useState(false);
  const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false);
  const [recordPromiseModalOpen, setRecordPromiseModalOpen] = useState(false);

  
  // Get selected case for actions
  const selectedCase = useMemo(() => {
    if (selectedRows.size === 0) return null;
    const firstSelectedId = Array.from(selectedRows)[0];
    return cases.find(c => c.id === firstSelectedId) || null;
  }, [selectedRows, cases]);

  // Filter cases based on search
  const filteredCases = useMemo(() => {
    if (!searchQuery.trim()) return cases;
    
    const query = searchQuery.toLowerCase();
    return cases.filter(c => 
      c.id.toLowerCase().includes(query) ||
      c.caseId.toLowerCase().includes(query) ||
      c.nextAction?.toLowerCase().includes(query) ||
      c.status.toLowerCase().includes(query) ||
      c.strategy?.name.toLowerCase().includes(query)
    );
  }, [cases, searchQuery]);
  
  // Sort cases
  const sortedCases = useMemo(() => {
    if (!sortColumn) return filteredCases;
    
    return [...filteredCases].sort((a, b) => {
      let aVal: string | number | Date | null | undefined = a[sortColumn as keyof CollectionCase] as string | number | Date | null | undefined;
      let bVal: string | number | Date | null | undefined = b[sortColumn as keyof CollectionCase] as string | number | Date | null | undefined;
      
      // Handle nested properties
      if (sortColumn === 'strategy') {
        aVal = a.strategy?.name;
        bVal = b.strategy?.name;
      }
      
      // Handle null/undefined
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      
      // Handle dates
      if (sortColumn === 'nextActionDate' || sortColumn === 'createdAt' || sortColumn === 'updatedAt') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      
      // Handle numbers
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle strings
      const comparison = String(aVal).localeCompare(String(bVal));
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredCases, sortColumn, sortDirection]);
  
  // Paginate
  const paginatedCases = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedCases.slice(startIndex, startIndex + pageSize);
  }, [sortedCases, currentPage, pageSize]);
  
  const totalPages = Math.ceil(sortedCases.length / pageSize);
  
  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(new Set(paginatedCases.map(c => c.id)));
    } else {
      setSelectedRows(new Set());
    }
  };
  
  const handleSelectRow = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedRows(newSelected);
  };
  
  // Sort handler
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortColumn(null);
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  
  // Export handler - current view
  const handleExport = () => {
    try {
      // Simple CSV export
      const headers = visibleColumns.map(col => col.label).join(',');
      const rows = sortedCases.map(c => 
        visibleColumns.map(col => {
          const value = renderColumnValue(col.id, c);
          return typeof value === 'string' ? `"${value}"` : value;
        }).join(',')
      );
      const csv = [headers, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `collection-cases-${customerName}-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${sortedCases.length} collection cases`);
            } catch {
          toast.error("Failed to export collection cases");
        }
  };

  // Export handler - all columns
  const handleExportAllColumns = () => {
    try {
      exportCollectionCasesToExcel(sortedCases, `collection-cases-${customerName}-all-columns-${new Date().toISOString().split('T')[0]}.csv`);
      toast.success(`Exported ${sortedCases.length} collection cases with all columns`);
    } catch (error) {
      toast.error("Failed to export collection cases");
      console.error('Export error:', error);
    }
  };
  
  // Action handlers
  const handleLogCall = () => {
    if (!selectedCase) return;
    setLogCallModalOpen(true);
  };
  
  const handleSendEmail = () => {
    if (!selectedCase) return;
    setSendEmailModalOpen(true);
  };
  
  const handleRecordPromise = () => {
    if (!selectedCase) return;
    setRecordPromiseModalOpen(true);
  };
  
  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return <Badge className="bg-green-600 text-white hover:bg-green-700">Active</Badge>;
      case 'on hold':
        return <Badge className="bg-amber-500 text-white hover:bg-amber-600">On Hold</Badge>;
      case 'closed':
        return <Badge variant="secondary">Closed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };
  
  // Get priority badge
  const getPriorityBadge = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return <Badge variant="destructive">High</Badge>;
      case 'medium':
        return <Badge variant="default">Medium</Badge>;
      case 'low':
        return <Badge variant="secondary">Low</Badge>;
      default:
        return <Badge variant="outline">{priority}</Badge>;
    }
  };
  
  // Format date with overdue styling
  const formatDueDate = (date: Date | null) => {
    if (!date) return <span className="text-muted-foreground">-</span>;
    
    const dueDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    
    const isOverdue = dueDate < today;
    const isDueToday = dueDate.getTime() === today.getTime();
    
    if (isOverdue) {
      return <span className="text-red-600 font-semibold">{formatDate(date.toISOString())}</span>;
    } else if (isDueToday) {
      return <span className="text-amber-600 font-semibold">{formatDate(date.toISOString())}</span>;
    }
    return <span>{formatDate(date.toISOString())}</span>;
  };
  
  // Render column value (for export)
  const renderColumnValue = (columnId: string, caseItem: CollectionCase): string => {
    switch (columnId) {
      case 'caseId':
        return caseItem.caseId;
      case 'amount':
        return caseItem.amount.toString();
      case 'daysInCollection':
        return caseItem.daysInCollection.toString();
      case 'nextAction':
        return caseItem.nextAction || '-';
      case 'nextActionDate':
        return caseItem.nextActionDate ? formatDate(caseItem.nextActionDate.toISOString()) : '-';
      case 'status':
        return caseItem.status;
      case 'strategy':
        return caseItem.strategy?.name || '-';
      case 'strategyPhase':
        if (!caseItem.strategyPhase) return '-';
        return `Phase ${caseItem.strategyPhase.currentPhase} of ${caseItem.strategyPhase.totalPhases}`;
      case 'priority':
        return caseItem.priority;
      case 'customerName':
        return caseItem.customerName;
      case 'assignedTo':
        return caseItem.assignedTo || '-';
      case 'createdAt':
        return formatDate(caseItem.createdAt.toISOString());
      case 'updatedAt':
        return formatDate(caseItem.updatedAt.toISOString());
      default:
        return '-';
    }
  };
  
  // Render column based on preferences
  const renderColumn = (columnId: string, caseItem: CollectionCase) => {
    switch (columnId) {
      case 'companyId':
        // In consolidated view, simulate different company IDs
        // In real implementation, this would come from the API
        return `COMP-${Math.floor(Math.random() * 3) + 1}`;
      case 'caseId':
        return (
          <button
            onClick={() => {
              navigate(`/collections/queue/case/${caseItem.caseId}`, {
                state: { from: "customer-collections" }
              });
            }}
            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
          >
            {caseItem.caseId}
          </button>
        );
      case 'amount':
        return formatCurrency(caseItem.amount, 'GBP');
      case 'daysInCollection':
        return caseItem.daysInCollection;
      case 'nextAction':
        return caseItem.nextAction || '-';
      case 'nextActionDate':
        return formatDueDate(caseItem.nextActionDate);
      case 'status':
        return getStatusBadge(caseItem.status);
      case 'strategy':
        return caseItem.strategy?.name || '-';
      case 'strategyPhase':
        if (!caseItem.strategyPhase) return '-';
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <span className="cursor-help">
                  Phase {caseItem.strategyPhase.currentPhase} of {caseItem.strategyPhase.totalPhases}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{caseItem.strategyPhase.phaseName}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      case 'priority':
        return getPriorityBadge(caseItem.priority);
      case 'customerName':
        return caseItem.customerName;
      case 'assignedTo':
        return caseItem.assignedTo || '-';
      case 'createdAt':
        return formatDate(caseItem.createdAt.toISOString());
      case 'updatedAt':
        return formatDate(caseItem.updatedAt.toISOString());
      default:
        return '-';
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-muted-foreground">Loading collection cases...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base font-medium">
            Collection Cases
            {searchQuery && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({filteredCases.length} of {cases.length})
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/collections/queue/create', { 
                state: { 
                  from: 'customer',
                  prePopulatedCustomer: {
                    customerId,
                    customerName,
                    accountNumber
                  }
                } 
              })}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Create New Case
            </Button>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search cases..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 w-[250px]"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={sortedCases.length === 0}
                  title="Export"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExport}>
                  Export Current View
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportAllColumns}>
                  Export All Columns
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setColumnConfigOpen(true)}
              title="Columns"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {cases.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No collection cases found for this customer.</p>
            </div>
          ) : filteredCases.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No cases match your search criteria.</p>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedRows.size === paginatedCases.length && paginatedCases.length > 0}
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      {isConsolidatedView && (
                        <TableHead>Company ID</TableHead>
                      )}
                      {visibleColumns
                        .filter(col => col.id !== 'select')
                        .map(column => (
                          <TableHead
                            key={column.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => handleSort(column.id)}
                          >
                            <div className="flex items-center gap-1">
                              {column.label}
                              {sortColumn === column.id && (
                                <span className="text-xs">
                                  {sortDirection === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </div>
                          </TableHead>
                        ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedCases.map((caseItem) => (
                      <TableRow key={caseItem.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedRows.has(caseItem.id)}
                            onCheckedChange={(checked) => handleSelectRow(caseItem.id, checked as boolean)}
                          />
                        </TableCell>
                        {isConsolidatedView && (
                          <TableCell>{renderColumn('companyId', caseItem)}</TableCell>
                        )}
                        {visibleColumns
                          .filter(col => col.id !== 'select')
                          .map(column => (
                            <TableCell key={column.id}>
                              {renderColumn(column.id, caseItem)}
                            </TableCell>
                          ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Showing {((currentPage - 1) * pageSize) + 1} to{" "}
                    {Math.min(currentPage * pageSize, sortedCases.length)} of{" "}
                    {sortedCases.length} cases
                  </span>
                  <Select
                    value={pageSize.toString()}
                    onValueChange={(value) => {
                      setPageSize(Number(value));
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 / page</SelectItem>
                      <SelectItem value="25">25 / page</SelectItem>
                      <SelectItem value="50">50 / page</SelectItem>
                      <SelectItem value="100">100 / page</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Floating Quick Actions Menu */}
      {selectedRows.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 duration-300">
          <div className="bg-primary text-primary-foreground rounded-lg shadow-2xl border border-primary/20">
            <div className="flex items-center gap-4 px-6 py-4">
              {/* Selection Info */}
              <div className="flex items-center gap-3 border-r border-primary-foreground/20 pr-4">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30">
                      {selectedRows.size}
                    </Badge>
                    <span className="font-medium">
                      {selectedRows.size === 1 ? "case" : "cases"} selected
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-sm text-primary-foreground/80">
                    <span>
                      Total: <span className="font-semibold">{formatCurrency(
                        Array.from(selectedRows).reduce((sum, id) => {
                          const caseItem = cases.find(c => c.id === id);
                          return sum + (caseItem?.amount || 0);
                        }, 0),
                        'GBP'
                      )}</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleLogCall}
                  className="gap-2"
                >
                  <Phone className="h-4 w-4" />
                  Log Call
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleSendEmail}
                  className="gap-2"
                >
                  <Mail className="h-4 w-4" />
                  Send Email
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleRecordPromise}
                  className="gap-2"
                >
                  <FileText className="h-4 w-4" />
                  Record Promise
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedRows(new Set())}
                  className="gap-2 hover:bg-primary-foreground/20"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Column Config Dialog */}
      <ColumnConfigDialog
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onApply={savePreferences}
        onReset={resetToDefaults}
      />
      
      {/* Action Modals */}
      {selectedCase && (
        <>
          <LogCallModal
            open={logCallModalOpen}
            onOpenChange={setLogCallModalOpen}
            customerName={selectedCase.customerName}
            customerPhone={customerPhone || selectedCase.customerPhone}
          />
          
          <SendEmailModal
            open={sendEmailModalOpen}
            onOpenChange={setSendEmailModalOpen}
            customerName={selectedCase.customerName}
            customerEmail={selectedCase.customerEmail}
            customerId={selectedCase.customerId}
          />
          
          <RecordPromiseModal
            open={recordPromiseModalOpen}
            onOpenChange={setRecordPromiseModalOpen}
            customerName={selectedCase.customerName}
            caseId={selectedCase.caseId}
            caseAmount={selectedCase.amount}
          />
        </>
      )}


    </div>
  );
}
